import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useUserSettings from "../user-settings/use-user-settings";
import { UserBookmarkCountBy } from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-by";
import { UserSettingsKeys } from "constants/user-settings-keys";
import UserBookmarkSortOption from "models/enumerations/sorting/user-bookmarks/user-bookmark-sort-option";
import { BookmarkUtils } from "utilities/bookmark-utils";
import { MyLiNKTab } from "models/enumerations/my-link-tab";
import UserBookmarkCountSortOption from "models/enumerations/sorting/user-bookmarks/user-bookmark-count-sort-option";

const useMyLiNKDefaults = () => {
    const { createOrUpdate } = useUserSettings();
    const { globalState, setGlobalState } = useGlobalState();

    const updateOrCreateMyLiNKUserSetting = async (
        key: keyof typeof UserSettingsKeys,
        value: string | number
    ) => {
        const setting = globalState.getUserSetting(key);
        try {
            const response = await createOrUpdate(
                setting?.with({ value: value.toString() }),
                globalState.currentIdentity?.userId()!
            );
            if (!response) {
                throw new Error();
            }
            setGlobalState((prevGlobalState) =>
                prevGlobalState.updateUserSetting(response)
            );
        } catch (error) {
            console.error(error);
            // TODO: Do we want to show an error message?
            // ToastManager.error(t("errors-actionResource", {action: t("updating"), item: t("accountSettings")}))
        }
    };

    const defaultMyLiNKTab =
        globalState.getUserSettingValue<UserBookmarkCountBy>(
            UserSettingsKeys.MyLiNKDefaultTab as keyof typeof UserSettingsKeys
        ) ?? MyLiNKTab.BookmarksAndCollections;

    const setDefaultMyLiNKTab = async (newValue) => {
        await updateOrCreateMyLiNKUserSetting(
            UserSettingsKeys.MyLiNKDefaultTab as keyof typeof UserSettingsKeys,
            newValue
        );
    };

    const defaultBookmarkViewByPillRoute = BookmarkUtils.getViewByRoute(
        globalState.getUserSettingValue<UserBookmarkCountBy>(
            UserSettingsKeys.MyLiNKBookmarksDefaultPill as keyof typeof UserSettingsKeys
        ) ?? UserBookmarkCountBy.All
    );

    const setDefaultBookmarkViewByPill = async (newValue) => {
        await updateOrCreateMyLiNKUserSetting(
            UserSettingsKeys.MyLiNKBookmarksDefaultPill as keyof typeof UserSettingsKeys,
            newValue
        );
    };

    const getBookmarkSortUserSettingForGrouping = (
        grouping?: UserBookmarkCountBy
    ) => {
        return globalState.getUserSettingValue<UserBookmarkCountSortOption>(
            getBookmarkSortUserSettingKey(grouping)
        );
    };

    const getBookmarkSortUserSettingKey = (
        grouping?: UserBookmarkCountBy
    ): keyof typeof UserSettingsKeys => {
        switch (grouping) {
            case UserBookmarkCountBy.Publication:
                return UserSettingsKeys.MyLiNKBookmarksDefaultPublicationsSort as keyof typeof UserSettingsKeys;

            case UserBookmarkCountBy.Color:
                return UserSettingsKeys.MyLiNKBookmarksDefaultColorSort as keyof typeof UserSettingsKeys;

            case UserBookmarkCountBy.Collection:
                return UserSettingsKeys.MyLiNKBookmarksDefaultCollectionsSort as keyof typeof UserSettingsKeys;

            default:
                return UserSettingsKeys.MyLiNKBookmarksDefaultAllSort as keyof typeof UserSettingsKeys;
        }
    };

    const setBookmarkSortUserSettingForGrouping = async (
        newValue: UserBookmarkSortOption,
        grouping?: UserBookmarkCountBy
    ) => {
        await updateOrCreateMyLiNKUserSetting(
            getBookmarkSortUserSettingKey(
                grouping
            ) as keyof typeof UserSettingsKeys,
            newValue
        );
    };

    return {
        defaultBookmarkViewByPillRoute,
        defaultMyLiNKTab,
        getBookmarkSortUserSettingForGrouping,
        setBookmarkSortUserSettingForGrouping,
        setDefaultBookmarkViewByPill,
        setDefaultMyLiNKTab,
    };
};

export default useMyLiNKDefaults;
